// A spatial index over the snap set: what lies near a point, in the order the
// set lists it. The snap set is built once per drawing revision (main.ts
// `snapSet`); a pointer move asks the index for what is within its aperture
// instead of walking every candidate (a 40 000-line plan made each Line-tool move
// cost 27 ms; the frame is 16.7).
//
// A uniform grid: an item goes into every cell its box covers, an item covering
// too many cells (a long line across the plan) into a list every query reads.
// `near` returns exactly the items within `r` (a point's distance, an edge's
// distance to its run), sorted by their index in the set, so a caller that took
// the first of two equal candidates still takes the same one.
// DOM-free (test/snapIndex.test.mjs).

export interface Vec2 { x: number; y: number }

export interface SnapIndex<T> {
  /** Every item, as given. */
  readonly all: readonly T[];
  /** The items within `r` of `p`, in the order of `all`. */
  near(p: Vec2, r: number): T[];
}

/** A cell holds this many items on average. */
const PER_CELL = 4;
/** An item covering more cells than this is read by every query instead. */
const MAX_CELLS = 64;

interface Box { x0: number; y0: number; x1: number; y1: number }

function gridIndex<T>(all: readonly T[], boxOf: (t: T) => Box | null, dist: (t: T, p: Vec2) => number): SnapIndex<T> {
  const boxes: (Box | null)[] = all.map((t) => {
    const b = boxOf(t);
    return b && [b.x0, b.y0, b.x1, b.y1].every(Number.isFinite) ? b : null;
  });
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity, n = 0;
  for (const b of boxes) if (b) { n++; x0 = Math.min(x0, b.x0); y0 = Math.min(y0, b.y0); x1 = Math.max(x1, b.x1); y1 = Math.max(y1, b.y1); }
  const span = Math.max(x1 - x0, y1 - y0, 1);
  const cell = n ? Math.max(span / Math.sqrt(n / PER_CELL), span / 4096, 1e-3) : 1;
  const cells = new Map<number, number[]>();
  const wide: number[] = [];
  const W = 1 << 20;
  const cx = (x: number): number => Math.floor((x - x0) / cell);
  const cy = (y: number): number => Math.floor((y - y0) / cell);
  boxes.forEach((b, i) => {
    if (!b) return;
    const i0 = cx(b.x0), i1 = cx(b.x1), j0 = cy(b.y0), j1 = cy(b.y1);
    if ((i1 - i0 + 1) * (j1 - j0 + 1) > MAX_CELLS) { wide.push(i); return; }
    for (let i2 = i0; i2 <= i1; i2++) for (let j = j0; j <= j1; j++) {
      const k = i2 * W + j;
      const list = cells.get(k);
      if (list) list.push(i); else cells.set(k, [i]);
    }
  });
  const seen = new Int32Array(all.length);
  let stamp = 0;
  return {
    all,
    near(p, r) {
      if (!n || !(r >= 0)) return [];
      stamp++;
      const hits: number[] = [];
      const take = (i: number): void => {
        if (seen[i] === stamp) return;
        seen[i] = stamp;
        if (dist(all[i], p) <= r) hits.push(i);
      };
      for (const i of wide) take(i);
      const i0 = cx(p.x - r), i1 = cx(p.x + r), j0 = cy(p.y - r), j1 = cy(p.y + r);
      if ((i1 - i0 + 1) * (j1 - j0 + 1) > cells.size) {
        for (const list of cells.values()) for (const i of list) take(i);
      } else {
        for (let i = i0; i <= i1; i++) for (let j = j0; j <= j1; j++) for (const k of cells.get(i * W + j) ?? []) take(k);
      }
      hits.sort((a, b) => a - b);
      return hits.map((i) => all[i]);
    },
  };
}

/** An index over points (`{ p }`). */
export function pointIndex<T extends { p: Vec2 }>(all: readonly T[]): SnapIndex<T> {
  return gridIndex(all, (t) => ({ x0: t.p.x, y0: t.p.y, x1: t.p.x, y1: t.p.y }), (t, q) => Math.hypot(t.p.x - q.x, t.p.y - q.y));
}

/** Distance from `p` to the run a–b. */
export function segmentDistance(p: Vec2, a: Vec2, b: Vec2): number {
  const dx = b.x - a.x, dy = b.y - a.y, l2 = dx * dx + dy * dy;
  const t = l2 ? Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / l2)) : 0;
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

/** An index over runs (`[a, b, …]`). */
export function edgeIndex<T extends readonly [Vec2, Vec2, ...unknown[]]>(all: readonly T[]): SnapIndex<T> {
  return gridIndex(
    all,
    (e) => ({ x0: Math.min(e[0].x, e[1].x), y0: Math.min(e[0].y, e[1].y), x1: Math.max(e[0].x, e[1].x), y1: Math.max(e[0].y, e[1].y) }),
    (e, q) => segmentDistance(q, e[0], e[1]),
  );
}
